import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import {
  TbAlertTriangle,
  TbCloudUpload,
  TbCopy,
  TbExternalLink,
  TbLink,
  TbList,
  TbMail,
  TbPlus,
  TbSearch,
} from "react-icons/tb";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import { useToast } from "../../../../../Hooks/toast";
import {
  entityDocumentHasUploadedContent,
  isValutMatrixConsolePin,
  sendBaasEntityVeriffSessionEmail,
  syncBaasEntityVeriffSession,
  pushBaasEntityPaySmartDocuments,
} from "../../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceFeatures } from "../../../../../Services/baasInstancesApi";
import { BAAS_ONBOARD_PATH } from "../../constants";
import { BaasEntidadeKycInvitesPanel } from "./BaasEntidadeKycInvitesPanel";
import { BaasEntidadeKycGenerateModal } from "./BaasEntidadeKycGenerateModal";
import { BaasEntidadeDocumentDeclarationCard } from "./BaasEntidadeDocumentDeclarationCard";
import { BaasEntidadeNewDocumentModal } from "./BaasEntidadeNewDocumentModal";
import { ValutDialog } from "../../../../../Components/Molecules/ValutDialog";
import {
  extractDocumentCountryCode,
} from "../baasIdentityDocumentLabels";
import { resolveNationalIdScheme } from "../baasIdentitySchemes";
import {
  buildBusinessFileChecklist,
  buildExistingDeclarationPickerIds,
  formatDocumentDate,
  formatDocumentTypeLabel,
  getDocumentTypeKey,
  hasForeignDriversLicenseIdentity,
  hasUploadedContent,
  resolveBusinessDeclarationSlots,
  resolvePersonDeclarationSlots,
} from "../baasEntidadeDocumentUtils";

const KYC_INVITE_FEATURE = "kyc.invite_link";

const toolbarBtnClass =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-gold/10 disabled:cursor-not-allowed disabled:opacity-50 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const PROVIDER_LABEL_KEYS = {
  veriff: "baas.entidades.detail.verifications.providerVeriff",
  sumsub: "baas.entidades.detail.verifications.providerSumsub",
  documents_upload: "baas.entidades.detail.verifications.providerDocumentsUpload",
  admin_manual: "baas.entidades.detail.verifications.providerAdminManual",
};

const statusBadgeStyles = {
  approved: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  in_review: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  rejected: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

const formatProviderLabel = (provider) => {
  const key = PROVIDER_LABEL_KEYS[String(provider || "").toLowerCase()];
  if (key && intl.get(key)) return intl.get(key);
  return provider || "—";
};

const InfoRow = ({ label, children, mono = false }) => (
  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <dt className="shrink-0 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </dt>
    <dd
      className={`min-w-0 text-sm text-brand-dark dark:text-gray-100 sm:text-right ${
        mono ? "font-mono text-xs break-all" : ""
      }`}
    >
      {children || "—"}
    </dd>
  </div>
);

const resolveProxyErrorMessage = (error) => {
  const detail =
    error?.detail
    || error?.response?.detail
    || error?.body?.detail
    || error?.data?.detail
    || error?.message;
  if (detail && String(detail).trim()) return String(detail).trim();
  return intl.get("baas.entidades.detail.verifications.syncError");
};

/**
 * Unified Verificações panel: KYC/Veriff (when permitted) + document declarations + stored files.
 */
export const BaasEntidadeVerificationsPanel = ({
  entity,
  documents = [],
  onViewDocument,
  embedded = false,
  instance = null,
  entityId = null,
  onSynced = null,
  allowSkipKyc = null,
  allowPaySmartImageSync = false,
  /** "pf" | "pj" — from DocumentsPanel wrapper. */
  entityType: entityTypeProp = null,
  uploadEnabled = false,
  canEditMeta = false,
  countryOptions = [],
  declarationDocs = [],
  uploadedDocs = [],
  existingProofByType = {},
  pendingDeclarationSlots = [],
  onAddDeclarationSlot = null,
  onSaveDeclaration = null,
  onDeleteDocument = null,
  onCompanyChecklistUpload = null,
  savingId = null,
  saveErrors = {},
  saveSuccesses = {},
  deletingId = null,
  deleteError = "",
  deleteSuccess = "",
  checklistUploadingType = "",
  checklistUploadError = "",
  checklistUploadSuccess = "",
  lepFeatureEnabled = false,
  formatStoredFileLabel = null,
}) => {
  const history = useHistory();
  const { addToast } = useToast();
  const { isValutConfigAdmin, loading: valutAdminLoading } = useBaasPlatformCreateAccess();
  const { can, loading: capabilitiesLoading } = useBaasCapabilities();
  const isValutAdmin = Boolean(isValutConfigAdmin);
  const canCreateInvites = can("entities.create");
  const skipKycEnabled = Boolean(
    allowSkipKyc ?? instance?.allowSkipKyc ?? instance?.allow_skip_kyc,
  );

  const [copied, setCopied] = useState(false);
  const [copiedSessionUrl, setCopiedSessionUrl] = useState(false);
  const [sessionEmailBusy, setSessionEmailBusy] = useState(false);
  const [sessionInput, setSessionInput] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [syncSuccess, setSyncSuccess] = useState("");
  const [syncOpen, setSyncOpen] = useState(false);
  const [paySmartPushBusy, setPaySmartPushBusy] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [inviteRefreshToken, setInviteRefreshToken] = useState(0);
  const [inviteFeatureEnabled, setInviteFeatureEnabled] = useState(false);
  const [inviteFeatureLoading, setInviteFeatureLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  const kyc = entity?.kyc || null;
  const verificationStatus = entity?.verificationStatus || null;
  const kybStatus = entity?.kybStatus || "pending";
  const reviewReasons = Array.isArray(entity?.reviewReasons) ? entity.reviewReasons : [];

  const uploadedWithContent = useMemo(() => {
    if (uploadedDocs.length > 0) return uploadedDocs;
    return Array.isArray(documents)
      ? documents.filter(entityDocumentHasUploadedContent)
      : [];
  }, [documents, uploadedDocs]);

  const shellClass = embedded ? "space-y-4" : "mt-5 space-y-4";
  const cardClass = embedded
    ? "rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border"
    : "app-card p-6";

  const provider = kyc?.provider || null;
  const sessionId = kyc?.sessionId || null;
  const kycStatus = kyc?.status || null;
  const kycUrl = kyc?.url || null;

  const resolvedEntityId =
    entityId || entity?.id || entity?.entityId || entity?.raw?.entity_id || null;

  const rawEntityType = String(
    entityTypeProp || entity?.type || entity?.entityType || entity?.raw?.type || "",
  )
    .trim()
    .toUpperCase();
  const isPersonEntity =
    !rawEntityType || rawEntityType === "PERSON" || rawEntityType === "PF";
  const isBusinessEntity = rawEntityType === "BUSINESS" || rawEntityType === "PJ";

  const showKycChrome =
    isPersonEntity
    && canCreateInvites
    && inviteFeatureEnabled
    && skipKycEnabled
    && !capabilitiesLoading;

  const canShowGenerateCta = showKycChrome && !valutAdminLoading;
  const canShowViewLinks = showKycChrome;
  const showManualKycHint =
    isPersonEntity && !valutAdminLoading && !isValutAdmin && skipKycEnabled && showKycChrome;
  const canShowSyncUi = isPersonEntity && isValutAdmin && !valutAdminLoading;
  const canShowPaySmartImageSync =
    Boolean(allowPaySmartImageSync)
    && isValutConfigAdmin
    && !valutAdminLoading
    && isValutMatrixConsolePin(instance)
    && !entity?.isPeerInstanceOwner;

  const hasKycSession = Boolean(provider || sessionId || verificationStatus || kycStatus);

  const personDeclarationSlots = useMemo(
    () => resolvePersonDeclarationSlots(declarationDocs, pendingDeclarationSlots, uploadedWithContent),
    [declarationDocs, pendingDeclarationSlots, uploadedWithContent],
  );

  const businessDeclarationSlots = useMemo(
    () => resolveBusinessDeclarationSlots(declarationDocs),
    [declarationDocs],
  );

  const businessFileChecklist = useMemo(
    () => buildBusinessFileChecklist(documents),
    [documents],
  );

  const missingLepContract = useMemo(() => {
    if (!isBusinessEntity || !lepFeatureEnabled) return false;
    const articles = businessFileChecklist.find(
      (item) => item.value === "ARTICLES_OF_INCORPORATION",
    );
    return !articles?.hasFile;
  }, [isBusinessEntity, lepFeatureEnabled, businessFileChecklist]);

  const existingDeclarationPickerIds = useMemo(
    () => buildExistingDeclarationPickerIds([
      ...personDeclarationSlots,
      ...pendingDeclarationSlots,
    ]),
    [personDeclarationSlots, pendingDeclarationSlots],
  );

  const showDocumentSections =
    Boolean(onSaveDeclaration)
    || uploadEnabled
    || uploadedWithContent.length > 0
    || personDeclarationSlots.length > 0
    || businessDeclarationSlots.length > 0
    || businessFileChecklist.length > 0;

  useEffect(() => {
    setSessionInput(sessionId || "");
    setSyncError("");
    setSyncSuccess("");
    setCreateError("");
    setCreateSuccess("");
  }, [sessionId, resolvedEntityId]);

  useEffect(() => {
    let cancelled = false;
    if (!instance?.baasInstanceId || !canCreateInvites) {
      setInviteFeatureEnabled(false);
      setInviteFeatureLoading(false);
      return undefined;
    }

    setInviteFeatureLoading(true);
    fetchBaasInstanceFeatures(instance)
      .then((features) => {
        if (cancelled) return;
        const list = Array.isArray(features) ? features : [];
        const invite = list.find(
          (item) =>
            String(item?.featureCode || "")
              .trim()
              .toLowerCase() === KYC_INVITE_FEATURE,
        );
        setInviteFeatureEnabled(Boolean(invite?.enabled));
      })
      .catch(() => {
        if (!cancelled) setInviteFeatureEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setInviteFeatureLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instance, canCreateInvites]);

  const kybLabel = intl.get(`baas.entidades.kybStatus.${kybStatus}`) || kybStatus;

  const handleCopySession = async () => {
    if (!sessionId) return;
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleCopySessionUrl = async () => {
    if (!kycUrl) return;
    try {
      await navigator.clipboard.writeText(kycUrl);
      setCopiedSessionUrl(true);
      window.setTimeout(() => setCopiedSessionUrl(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleSendSessionEmail = async () => {
    if (!instance || !resolvedEntityId || !kycUrl || sessionEmailBusy) return;
    setSessionEmailBusy(true);
    try {
      const result = await sendBaasEntityVeriffSessionEmail(instance, resolvedEntityId, {
        sessionUrl: kycUrl,
        sessionId,
      });
      const sent = result?.sent === true || result?.Sent === true;
      addToast({
        type: sent ? "success" : "info",
        title: sent
          ? intl.get("baas.entidades.detail.verifications.sendSessionEmailReady")
          : intl.get("baas.entidades.detail.verifications.sendSessionEmailStub"),
      });
    } catch (error) {
      addToast({
        type: "info",
        title: intl.get("baas.entidades.detail.verifications.sendSessionEmailStub"),
        description: resolveProxyErrorMessage(error),
      });
    } finally {
      setSessionEmailBusy(false);
    }
  };

  const handleSync = async () => {
    if (!isValutAdmin || !instance || !resolvedEntityId || syncing) return;
    const trimmed = String(sessionInput || "").trim();
    if (!trimmed) {
      setSyncError(intl.get("baas.entidades.detail.verifications.syncSessionRequired"));
      setSyncSuccess("");
      return;
    }

    setSyncing(true);
    setSyncError("");
    setSyncSuccess("");
    try {
      const result = await syncBaasEntityVeriffSession(instance, resolvedEntityId, trimmed);
      const mediaCount = Number(result?.media_count ?? result?.mediaCount ?? 0);
      setSyncSuccess(
        mediaCount > 0
          ? intl.get("baas.entidades.detail.verifications.syncSuccessWithMedia", { count: mediaCount })
          : intl.get("baas.entidades.detail.verifications.syncSuccess"),
      );
      if (typeof onSynced === "function") {
        await onSynced(result);
      }
    } catch (error) {
      setSyncError(resolveProxyErrorMessage(error));
    } finally {
      setSyncing(false);
    }
  };

  const handlePushPaySmartImages = async () => {
    if (!canShowPaySmartImageSync || !instance || !resolvedEntityId || paySmartPushBusy) return;
    setPaySmartPushBusy(true);
    setCreateError("");
    setCreateSuccess("");
    try {
      const result = await pushBaasEntityPaySmartDocuments(instance, resolvedEntityId);
      const count = Number(result?.pushed_count ?? result?.pushedCount ?? 0);
      const message =
        count > 0
          ? intl.get("baas.entidades.detail.verifications.paySmartPushSuccessCount", { count })
          : intl.get("baas.entidades.detail.verifications.paySmartPushSuccess");
      setCreateSuccess(message);
      addToast({ type: "success", title: message });
      if (typeof onSynced === "function") {
        await onSynced(result);
      }
    } catch (error) {
      const message =
        resolveProxyErrorMessage(error)
        || intl.get("baas.entidades.detail.verifications.paySmartPushError");
      setCreateError(message);
      addToast({
        type: "error",
        title: intl.get("baas.entidades.detail.verifications.paySmartPushError"),
      });
    } finally {
      setPaySmartPushBusy(false);
    }
  };

  const resolveStoredLabel = (doc) => {
    if (typeof formatStoredFileLabel === "function") {
      return formatStoredFileLabel(doc);
    }
    const documentType = doc.document_id || doc.documentId || doc.description;
    return formatDocumentTypeLabel(documentType, {
      doc,
      countryCode: extractDocumentCountryCode(doc),
      countryOptions,
    });
  };

  const manualKycHintBlock = showManualKycHint ? (
    <p className="mt-4 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
      {intl.get("baas.entidades.detail.verifications.createSessionSkipKycHint")}
    </p>
  ) : null;

  const actionToolbar =
    canShowGenerateCta || canShowSyncUi || canShowViewLinks || canShowPaySmartImageSync ? (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {canShowGenerateCta ? (
            <button
              type="button"
              className={toolbarBtnClass}
              onClick={() => {
                setCreateError("");
                setCreateSuccess("");
                setGenerateOpen(true);
              }}
              disabled={inviteFeatureLoading || !instance || !resolvedEntityId}
            >
              <TbLink className="h-3.5 w-3.5" aria-hidden />
              {intl.get("baas.entidades.detail.verifications.generateButton")}
            </button>
          ) : null}
          {canShowSyncUi ? (
            <button
              type="button"
              className={toolbarBtnClass}
              onClick={() => {
                setSyncError("");
                setSyncSuccess("");
                setSyncOpen(true);
              }}
              disabled={!instance || !resolvedEntityId}
            >
              <TbSearch className="h-3.5 w-3.5" aria-hidden />
              {intl.get("baas.entidades.detail.verifications.syncTitle")}
            </button>
          ) : null}
          {canShowPaySmartImageSync ? (
            <button
              type="button"
              className={toolbarBtnClass}
              title={intl.get("baas.entidades.detail.verifications.paySmartPushHint")}
              onClick={() => {
                void handlePushPaySmartImages();
              }}
              disabled={paySmartPushBusy || !instance || !resolvedEntityId}
            >
              <TbCloudUpload className="h-3.5 w-3.5" aria-hidden />
              {paySmartPushBusy
                ? intl.get("baas.entidades.detail.verifications.paySmartPushBusy")
                : intl.get("baas.entidades.detail.verifications.paySmartPush")}
            </button>
          ) : null}
        </div>
        {canShowViewLinks ? (
          <button
            type="button"
            className={toolbarBtnClass}
            onClick={() => history.push(BAAS_ONBOARD_PATH)}
          >
            <TbList className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.entidades.detail.verifications.viewLinks")}
          </button>
        ) : null}
      </div>
    ) : null;

  const generateModal = (
    <BaasEntidadeKycGenerateModal
      open={generateOpen}
      onClose={() => setGenerateOpen(false)}
      instance={instance}
      entityId={resolvedEntityId}
      canCreateInvite={canCreateInvites}
      inviteFeatureEnabled={inviteFeatureEnabled}
      onGenerated={async () => {
        setInviteRefreshToken((n) => n + 1);
      }}
      onViewAllLinks={() => {
        setGenerateOpen(false);
        history.push(BAAS_ONBOARD_PATH);
      }}
    />
  );

  const newDocumentModal = (
    <BaasEntidadeNewDocumentModal
      open={newDocOpen}
      onClose={() => setNewDocOpen(false)}
      countryOptions={countryOptions}
      existingPickerIds={existingDeclarationPickerIds}
      onConfirm={onAddDeclarationSlot}
    />
  );

  const syncModal = (
    <ValutDialog
      open={syncOpen}
      onClose={() => {
        if (syncing) return;
        setSyncOpen(false);
      }}
      title={intl.get("baas.entidades.detail.verifications.syncTitle")}
      icon={<TbSearch className="h-4 w-4" />}
      maxWidth="sm"
      disableBackdropClose={syncing}
      footer={
        <>
          <button
            type="button"
            className="btn-outline w-full sm:w-auto"
            onClick={() => setSyncOpen(false)}
            disabled={syncing}
          >
            {intl.get("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            onClick={() => {
              void handleSync();
            }}
            disabled={syncing || !instance || !resolvedEntityId || !String(sessionInput || "").trim()}
          >
            {syncing
              ? intl.get("baas.entidades.detail.verifications.syncing")
              : intl.get("baas.entidades.detail.verifications.syncButton")}
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {intl.get("baas.entidades.detail.verifications.syncHint")}
      </p>
      <input
        type="text"
        value={sessionInput}
        onChange={(event) => setSessionInput(event.target.value)}
        placeholder={intl.get("baas.entidades.detail.verifications.syncPlaceholder")}
        className="mt-3 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-xs text-brand-dark outline-none focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
        disabled={syncing || !instance || !resolvedEntityId}
        autoComplete="off"
        spellCheck={false}
      />
      {syncError ? <p className="mt-3 text-xs text-red-500">{syncError}</p> : null}
      {syncSuccess ? (
        <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400">{syncSuccess}</p>
      ) : null}
    </ValutDialog>
  );

  const invitesBlock = showKycChrome ? (
    <BaasEntidadeKycInvitesPanel
      instance={instance}
      entityId={resolvedEntityId}
      isPersonEntity={isPersonEntity}
      refreshToken={inviteRefreshToken}
    />
  ) : null;

  const kycStatusCard = isPersonEntity ? (
    <div className={cardClass}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.verifications.title")}
        </h2>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            statusBadgeStyles[kybStatus] ?? statusBadgeStyles.pending
          }`}
        >
          {kybLabel}
        </span>
      </div>

      {hasKycSession ? (
        <dl className="space-y-3">
          <InfoRow label={intl.get("baas.entidades.detail.verifications.verificationStatus")}>
            {verificationStatus || "—"}
          </InfoRow>
          <InfoRow label={intl.get("baas.entidades.detail.verifications.provider")}>
            {formatProviderLabel(provider)}
          </InfoRow>
          <InfoRow label={intl.get("baas.entidades.detail.verifications.kycStatus")}>
            {kycStatus || "—"}
          </InfoRow>
          <InfoRow label={intl.get("baas.entidades.detail.verifications.sessionId")} mono>
            {sessionId ? (
              <span className="inline-flex max-w-full items-center gap-2">
                <span className="break-all">{sessionId}</span>
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-500 transition hover:border-[#e7a73f] hover:bg-brand-gold/10 dark:border-brand-dark-border dark:text-gray-300"
                  onClick={handleCopySession}
                  title={intl.get("baas.entidades.detail.verifications.copySessionId")}
                >
                  <TbCopy className="h-3.5 w-3.5" aria-hidden />
                  {copied
                    ? intl.get("baas.entidades.detail.verifications.copied")
                    : intl.get("baas.entidades.detail.verifications.copy")}
                </button>
              </span>
            ) : (
              "—"
            )}
          </InfoRow>
          {kycUrl ? (
            <InfoRow label={intl.get("baas.entidades.detail.verifications.sessionUrl")}>
              <span className="inline-flex max-w-full flex-wrap items-center justify-end gap-2">
                <a
                  href={kycUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={toolbarBtnClass}
                >
                  <TbExternalLink className="h-3.5 w-3.5" aria-hidden />
                  {intl.get("baas.entidades.detail.verifications.openSession")}
                </a>
                <button
                  type="button"
                  className={toolbarBtnClass}
                  onClick={() => {
                    void handleCopySessionUrl();
                  }}
                >
                  <TbCopy className="h-3.5 w-3.5" aria-hidden />
                  {copiedSessionUrl
                    ? intl.get("baas.entidades.detail.verifications.copied")
                    : intl.get("baas.entidades.detail.verifications.copySession")}
                </button>
                <button
                  type="button"
                  className={toolbarBtnClass}
                  onClick={() => {
                    void handleSendSessionEmail();
                  }}
                  disabled={sessionEmailBusy || !instance || !resolvedEntityId}
                >
                  <TbMail className="h-3.5 w-3.5" aria-hidden />
                  {sessionEmailBusy
                    ? intl.get("baas.entidades.detail.verifications.sendSessionEmailBusy")
                    : intl.get("baas.entidades.detail.verifications.sendSessionEmail")}
                </button>
              </span>
            </InfoRow>
          ) : null}
        </dl>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.verifications.empty")}
        </p>
      )}

      {manualKycHintBlock}
      {createError ? <p className="mt-2 text-xs text-red-500">{createError}</p> : null}
      {createSuccess ? (
        <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{createSuccess}</p>
      ) : null}

      {String(provider || "").toLowerCase() === "veriff" && !isValutAdmin ? (
        <p className="mt-4 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          {intl.get("baas.entidades.detail.verifications.veriffImagesHint")}
        </p>
      ) : null}
    </div>
  ) : (
    <div className={cardClass}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.verifications.title")}
        </h2>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            statusBadgeStyles[kybStatus] ?? statusBadgeStyles.pending
          }`}
        >
          {kybLabel}
        </span>
      </div>
      <p className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
        {intl.get("baas.entidades.detail.verifications.businessKycNotApplicable")}
      </p>
      {createError ? <p className="mt-2 text-xs text-red-500">{createError}</p> : null}
      {createSuccess ? (
        <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{createSuccess}</p>
      ) : null}
      <dl className="mt-4 space-y-3">
        <InfoRow label={intl.get("baas.entidades.detail.verifications.verificationStatus")}>
          {verificationStatus || intl.get("baas.entidades.kybStatus.pending") || "pending"}
        </InfoRow>
        <InfoRow label={intl.get("baas.entidades.detail.verifications.provider")}>
          {intl.get("baas.entidades.detail.verifications.businessProviderNa")}
        </InfoRow>
        <InfoRow label={intl.get("baas.entidades.detail.verifications.sessionId")} mono>
          {intl.get("baas.entidades.detail.verifications.businessSessionNa")}
        </InfoRow>
      </dl>
    </div>
  );

  const businessChecklistSection = isBusinessEntity && showDocumentSections ? (
    <div className={cardClass}>
      <div>
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.businessDocumentsChecklistTitle")}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.businessDocumentsChecklistHint")}
        </p>
      </div>
      {missingLepContract ? (
        <div
          className="mt-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2.5 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100"
          role="alert"
        >
          <TbAlertTriangle className="mt-0.5 h-4 w-5 shrink-0" aria-hidden />
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm font-semibold">
              {intl.get("baas.entidades.detail.lepContractRequiredTitle")}
            </p>
            <p className="text-xs opacity-90">
              {intl.get("baas.entidades.detail.lepContractRequiredHint")}
            </p>
          </div>
        </div>
      ) : null}
      <ul className="mb-0 mt-3 divide-y divide-gray-100 dark:divide-brand-dark-border">
        {businessFileChecklist.map((item) => (
          <li
            key={item.value}
            className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-brand-dark dark:text-gray-100">
                {item.labelKey && intl.get(item.labelKey)
                  ? intl.get(item.labelKey)
                  : formatDocumentTypeLabel(item.value)}
                {lepFeatureEnabled && item.value === "ARTICLES_OF_INCORPORATION" ? (
                  <span className="ml-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    {intl.get("baas.entidades.detail.lepContractRequiredBadge")}
                  </span>
                ) : null}
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {item.hasFile
                  ? intl.get("baas.entidades.detail.businessChecklistStatusUploaded")
                  : item.declared
                    ? intl.get("baas.entidades.detail.businessChecklistStatusDeclared")
                    : intl.get("baas.entidades.detail.businessChecklistStatusPending")}
                {item.number ? ` · ${item.number}` : ""}
                {item.existing?.file_name || item.existing?.fileName
                  ? ` · ${item.existing.file_name || item.existing.fileName}`
                  : ""}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {item.hasFile ? (
                <button
                  type="button"
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-gold/10 dark:border-brand-dark-border dark:text-gray-100"
                  onClick={() => onViewDocument?.(item.existing)}
                >
                  {intl.get("baas.entidades.detail.viewDocument")}
                </button>
              ) : null}
              {uploadEnabled ? (
                <label className="inline-flex cursor-pointer items-center rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-gold/10 dark:border-brand-dark-border dark:text-gray-100">
                  <span>
                    {checklistUploadingType === item.value
                      ? intl.get("baas.entidades.detail.uploading")
                      : item.hasFile
                        ? intl.get("baas.entidades.detail.businessChecklistReplace")
                        : intl.get("baas.entidades.detail.businessChecklistUpload")}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="sr-only"
                    disabled={Boolean(checklistUploadingType)}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      event.target.value = "";
                      if (file) onCompanyChecklistUpload?.(item.value, file);
                    }}
                  />
                </label>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {checklistUploadError ? <p className="mt-2 text-sm text-red-500">{checklistUploadError}</p> : null}
      {checklistUploadSuccess ? (
        <p className="mt-2 text-sm text-emerald-600">{checklistUploadSuccess}</p>
      ) : null}
    </div>
  ) : null;

  const declarationSection = showDocumentSections ? (
    <div className={cardClass}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.verifications.declaredDocuments")}
        </h3>
        {isPersonEntity && canEditMeta && typeof onAddDeclarationSlot === "function" ? (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-gold/10 dark:border-brand-dark-border dark:text-gray-100"
            onClick={() => setNewDocOpen(true)}
          >
            <TbPlus className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.entidades.detail.declararNovoDocumento")}
          </button>
        ) : null}
      </div>

      <div className="divide-y divide-gray-100 dark:divide-brand-dark-border">
        {isPersonEntity
          ? personDeclarationSlots.map((slot) => {
              const slotCountry = extractDocumentCountryCode(slot.doc) || slot.forcedCountryCode;
              const saveKey = slot.doc.id || slot.type;
              return (
                <BaasEntidadeDocumentDeclarationCard
                  key={saveKey}
                  variant="sectionItem"
                  identityVariant={slot.identityVariant || null}
                  doc={slot.doc}
                  countryOptions={countryOptions}
                  canEdit={canEditMeta}
                  saving={savingId === saveKey}
                  saveError={saveErrors[saveKey]}
                  saveSuccess={saveSuccesses[saveKey]}
                  onSave={onSaveDeclaration}
                  forcedCountryCode={slot.forcedCountryCode || null}
                  defaultOpen={Boolean(slot.forcedCountryCode || slot.doc.number || slot.doc.id)}
                  hint={
                    slot.type === "SSN" || slot.identityVariant === "itin"
                        ? intl.get("baas.entidades.detail.taxIdDeclarationHint")
                      : slot.type === "NATIONAL_ID"
                      ? slotCountry
                        ? intl.get(resolveNationalIdScheme(slotCountry).locale.pathHintKey)
                        : intl.get("baas.entidades.detail.documentsUploadCountryFirstHint")
                      : slot.type === "PASSPORT"
                        ? intl.get("baas.entidades.detail.documentsPassportPathHint")
                        : slot.type === "DRIVERS_LICENSE"
                          ? intl.get("baas.entidades.detail.documentsDriversLicensePathHint")
                          : null
                  }
                  existingProofByType={existingProofByType}
                  uploadEnabled={uploadEnabled}
                />
              );
            })
          : businessDeclarationSlots.map((slot) => {
              const saveKey = slot.doc.id || slot.type;
              return (
                <BaasEntidadeDocumentDeclarationCard
                  key={saveKey}
                  doc={slot.doc}
                  countryOptions={countryOptions}
                  canEdit={canEditMeta}
                  saving={savingId === saveKey}
                  saveError={saveErrors[saveKey]}
                  saveSuccess={saveSuccesses[saveKey]}
                  onSave={onSaveDeclaration}
                  asAccordion
                  defaultOpen={Boolean(slot.doc.number || slot.doc.id)}
                  hint={intl.get("baas.entidades.detail.businessDeclarationMetaHint")}
                  existingProofByType={existingProofByType}
                  uploadEnabled={uploadEnabled}
                />
              );
            })}

        {isPersonEntity && personDeclarationSlots.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.entidades.detail.documentsEmpty")}
          </p>
        ) : null}
      </div>
    </div>
  ) : null;

  const storedFilesSection =
    showDocumentSections && uploadedWithContent.length > 0 ? (
      <div className={cardClass}>
        <h3 className="mb-1 text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.verifications.storedFiles")}
        </h3>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.detail.verifications.storedFilesHint")}
        </p>
        <ul className="mb-0 divide-y divide-gray-100 dark:divide-brand-dark-border">
          {uploadedWithContent.map((doc) => {
            const documentType = doc.document_id || doc.documentId || doc.description;
            const canView = hasUploadedContent(doc);
            const canDelete = Boolean(uploadEnabled && doc?.id && canView && onDeleteDocument);
            return (
              <li
                key={doc.id || documentType}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex flex-col gap-0.5">
                  <span className="font-medium text-brand-dark dark:text-gray-100">
                    {resolveStoredLabel(doc)}
                  </span>
                  {doc.number ? (
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      {doc.number}
                    </span>
                  ) : null}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {canView
                      ? formatDocumentDate(doc.created_at || doc.createdAt) || "—"
                      : intl.get("baas.entidades.detail.businessChecklistStatusDeclared")}
                  </span>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {canView && typeof onViewDocument === "function" ? (
                    <button
                      type="button"
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-brand-dark transition hover:border-[#e7a73f] hover:bg-brand-gold/10 dark:border-brand-dark-border dark:text-gray-100"
                      onClick={() => onViewDocument(doc)}
                    >
                      {intl.get("baas.entidades.detail.viewDocument")}
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
                      disabled={deletingId === doc.id}
                      onClick={() => onDeleteDocument(doc)}
                    >
                      {deletingId === doc.id
                        ? intl.get("baas.entidades.detail.deletingDocument")
                        : intl.get("baas.entidades.detail.deleteDocumentProof")}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        {deleteError ? <p className="mt-3 text-sm text-red-500">{deleteError}</p> : null}
        {deleteSuccess ? <p className="mt-3 text-sm text-emerald-600">{deleteSuccess}</p> : null}
      </div>
    ) : null;

  const reviewReasonsSection =
    reviewReasons.length > 0 ? (
      <div className={cardClass}>
        <h3 className="mb-3 text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.entidades.detail.verifications.reviewReasons")}
        </h3>
        <ul className="mb-0 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-300">
          {reviewReasons.map((reason, index) => (
            <li key={`${reason}-${index}`}>{String(reason)}</li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <div className={shellClass}>
      {isPersonEntity && (showKycChrome || canShowSyncUi || canShowPaySmartImageSync) ? actionToolbar : null}
      {generateModal}
      {newDocumentModal}
      {syncModal}

      {kycStatusCard}
      {invitesBlock}
      {businessChecklistSection}
      {declarationSection}
      {storedFilesSection}
      {reviewReasonsSection}
    </div>
  );
};

export default BaasEntidadeVerificationsPanel;
